import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import { FiPlus, FiMessageSquare, FiTrash2, FiEdit2, FiSettings, FiLogOut, FiChevronLeft, FiSun, FiMoon, FiZap, FiHeart, FiChrome, FiX } from 'react-icons/fi';
import { useTheme } from '../../context/ThemeContext';
import CreditPlans from '../common/CreditPlans';

export default function Sidebar({ currentChatId, onSelectChat, onNewChat, isOpen, onToggle }) {
  const [chats, setChats] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [showPlans, setShowPlans] = useState(false);
  const [extDismissed, setExtDismissed] = useState(() => localStorage.getItem('rc_ext_dismissed') === '1');
  const { user, logout } = useAuth();

  const dismissExtBanner = (e) => {
    e.stopPropagation();
    localStorage.setItem('rc_ext_dismissed', '1');
    setExtDismissed(true);
  };
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) fetchChats();
  }, [user]);

  const fetchChats = async () => {
    try {
      const { data } = await api.get('/chat');
      setChats(data);
    } catch (err) {
      // silent
    }
  };

  const handleDelete = async (e, chatId) => {
    e.stopPropagation();
    try {
      await api.delete(`/chat/${chatId}`);
      setChats(prev => prev.filter(c => c._id !== chatId));
      if (currentChatId === chatId) onNewChat();
    } catch (err) {
      // silent
    }
  };

  const handleRename = async (chatId) => {
    if (!editTitle.trim()) return setEditingId(null);
    try {
      await api.put(`/chat/${chatId}`, { title: editTitle });
      setChats(prev => prev.map(c => c._id === chatId ? { ...c, title: editTitle } : c));
    } catch (err) {
      // silent
    }
    setEditingId(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <>
      {isOpen && <div onClick={onToggle} style={styles.overlay} />}

      <aside style={{ ...styles.sidebar, ...(isOpen ? styles.sidebarOpen : {}) }}>
        <div style={styles.header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <img src="/logo.png" alt="ReplyCraft" style={{ width: 28, height: 28, borderRadius: 6 }} />
              <h2 style={styles.brand}>ReplyCraft</h2>
            </div>
            <button onClick={toggleTheme} style={styles.themeToggleBtn} title={theme === 'dark' ? 'Light mode' : 'Dark mode'}>
              {theme === 'dark' ? <FiSun size={16} /> : <FiMoon size={16} />}
            </button>
          </div>
          <button onClick={onToggle} style={styles.closeBtn}><FiChevronLeft size={20} /></button>
        </div>

        <button onClick={onNewChat} style={styles.newChatBtn}>
          <FiPlus size={18} />
          <span>New Chat</span>
        </button>

        <div style={styles.chatList}>
          {chats.map(chat => (
            <div
              key={chat._id}
              onClick={() => onSelectChat(chat._id)}
              style={{
                ...styles.chatItem,
                ...(currentChatId === chat._id ? styles.chatItemActive : {})
              }}
            >
              <FiMessageSquare size={14} style={{ flexShrink: 0, marginTop: 2 }} />
              {editingId === chat._id ? (
                <input
                  value={editTitle}
                  onChange={e => setEditTitle(e.target.value)}
                  onBlur={() => handleRename(chat._id)}
                  onKeyDown={e => e.key === 'Enter' && handleRename(chat._id)}
                  autoFocus
                  style={styles.editInput}
                  onClick={e => e.stopPropagation()}
                />
              ) : (
                <span style={styles.chatTitle}>{chat.title}</span>
              )}
              <div style={styles.chatActions}>
                <button onClick={(e) => { e.stopPropagation(); setEditingId(chat._id); setEditTitle(chat.title); }} style={styles.actionBtn}><FiEdit2 size={12} /></button>
                <button onClick={(e) => handleDelete(e, chat._id)} style={styles.actionBtn}><FiTrash2 size={12} /></button>
              </div>
            </div>
          ))}
        </div>

        <div style={styles.footer}>
          {/* Chrome Extension Promo */}
          {!extDismissed && (
            <Link to="/extension" style={styles.extPromo}>
              <FiChrome size={16} color="var(--accent)" style={{ flexShrink: 0 }} />
              <div style={styles.extPromoText}>
                <div style={styles.extPromoTitle}>Get Chrome Extension</div>
                <div style={styles.extPromoSubtitle}>Reply inside Gmail</div>
              </div>
              <button onClick={dismissExtBanner} style={styles.extPromoClose} aria-label="Dismiss">
                <FiX size={14} />
              </button>
            </Link>
          )}

          {/* Credit Balance */}
          {user && (
            <button onClick={() => setShowPlans(true)} style={styles.creditBar}>
              <FiZap size={16} color="var(--accent)" />
              <span style={styles.creditText}>
                <strong>{(user.credits || 0).toLocaleString()}</strong> credits
              </span>
              <span style={styles.addCreditsLabel}>Add</span>
            </button>
          )}

          {user && (
            <button onClick={() => navigate('/saved-replies')} style={styles.savedRepliesBtn}>
              <FiHeart size={14} />
              <span>Saved Replies</span>
            </button>
          )}

          {user && (
            <div style={styles.userSection}>
              <div style={styles.avatar}>{user.name?.charAt(0).toUpperCase()}</div>
              <div style={styles.userInfo}>
                <span style={styles.userName}>{user.name}</span>
                <span style={styles.userCredits}>{(user.credits || 0).toLocaleString()} credits</span>
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                <button onClick={() => navigate('/dashboard')} style={styles.actionBtn}><FiSettings size={16} /></button>
                <button onClick={handleLogout} style={styles.actionBtn}><FiLogOut size={16} /></button>
              </div>
            </div>
          )}

          {!user && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '0 12px' }}>
              <button onClick={() => navigate('/login')} style={styles.loginBtn}>Log in</button>
              <button onClick={() => navigate('/signup')} style={styles.signupBtn}>Sign up free</button>
            </div>
          )}

          {/* Footer Links */}
          <div style={styles.footerLinks}>
            <Link to="/pricing" style={styles.footerLink}>Pricing</Link>
            <span style={styles.footerDot}>·</span>
            <Link to="/how-it-works" style={styles.footerLink}>How it Works</Link>
            <span style={styles.footerDot}>·</span>
            <Link to="/extension" style={styles.footerLink}>Extension</Link>
            <span style={styles.footerDot}>·</span>
            <Link to="/faq" style={styles.footerLink}>FAQ</Link>
            <span style={styles.footerDot}>·</span>
            <Link to="/blog" style={styles.footerLink}>Blog</Link>
            <span style={styles.footerDot}>·</span>
            <Link to="/contact" style={styles.footerLink}>Contact</Link>
            <span style={styles.footerDot}>·</span>
            <Link to="/privacy" style={styles.footerLink}>Privacy</Link>
            <span style={styles.footerDot}>·</span>
            <Link to="/terms" style={styles.footerLink}>Terms</Link>
          </div>
        </div>
      </aside>

      <CreditPlans isOpen={showPlans} onClose={() => setShowPlans(false)} currentCredits={user?.credits || 0} />
    </>
  );
}

const styles = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 998 },
  sidebar: {
    width: 'var(--sidebar-width)', height: '100vh', background: 'var(--bg-secondary)',
    borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column',
    position: 'fixed', left: 0, top: 0, zIndex: 999, transition: 'transform 0.3s ease',
    transform: 'translateX(-100%)'
  },
  sidebarOpen: { transform: 'translateX(0)' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 16px 8px' },
  brand: { fontSize: 18, fontWeight: 700, color: 'var(--accent)' },
  closeBtn: { background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 4, display: 'flex' },
  themeToggleBtn: {
    background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 6,
    color: 'var(--text-secondary)', cursor: 'pointer', padding: 6, display: 'flex',
    alignItems: 'center', justifyContent: 'center', transition: 'background 0.2s'
  },
  newChatBtn: {
    display: 'flex', alignItems: 'center', gap: 10, margin: '8px 12px', padding: '12px 16px',
    background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 10,
    color: 'var(--text-primary)', fontSize: 14, fontWeight: 500, cursor: 'pointer'
  },
  chatList: { flex: 1, overflowY: 'auto', padding: '8px 8px' },
  chatItem: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 14, color: 'var(--text-secondary)', transition: 'background 0.15s', position: 'relative' },
  chatItemActive: { background: 'var(--bg-primary)', color: 'var(--text-primary)' },
  chatTitle: { flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  editInput: { flex: 1, background: 'var(--bg-primary)', border: '1px solid var(--accent)', borderRadius: 4, color: 'var(--text-primary)', padding: '2px 6px', fontSize: 13, outline: 'none' },
  chatActions: { display: 'flex', gap: 2, opacity: 0.5 },
  actionBtn: { background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 4, display: 'flex', borderRadius: 4 },
  footer: { padding: '12px 0', borderTop: '1px solid var(--border)' },
  creditBar: {
    display: 'flex', alignItems: 'center', gap: 8, margin: '0 12px 10px', padding: '10px 14px',
    background: 'var(--accent-dim)', border: '1px solid var(--accent)', borderRadius: 10,
    cursor: 'pointer', width: 'calc(100% - 24px)', fontSize: 14, color: 'var(--text-primary)'
  },
  creditText: { flex: 1, fontSize: 13 },
  addCreditsLabel: { fontSize: 12, fontWeight: 700, color: 'var(--accent)', padding: '2px 8px', background: 'var(--bg-secondary)', borderRadius: 6 },
  savedRepliesBtn: {
    display: 'flex', alignItems: 'center', gap: 8, margin: '0 12px 8px', padding: '8px 14px',
    background: 'none', border: '1px solid var(--border)', borderRadius: 8,
    color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer', width: 'calc(100% - 24px)',
    transition: 'all 0.2s'
  },
  userSection: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px' },
  avatar: { width: 32, height: 32, borderRadius: '50%', background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: 14, flexShrink: 0 },
  userInfo: { flex: 1, display: 'flex', flexDirection: 'column' },
  userName: { fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' },
  userCredits: { fontSize: 11, color: 'var(--text-muted)' },
  loginBtn: { padding: '10px', background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 14, cursor: 'pointer' },
  signupBtn: { padding: '10px', background: 'var(--accent)', border: 'none', borderRadius: 8, color: 'white', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  footerLinks: { display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 4, padding: '10px 12px 4px', borderTop: '1px solid var(--border)', marginTop: 8 },
  footerLink: { color: 'var(--text-muted)', fontSize: 11, textDecoration: 'none' },
  footerDot: { color: 'var(--text-muted)', fontSize: 11 },
  extPromo: {
    display: 'flex', alignItems: 'center', gap: 10, margin: '0 12px 10px', padding: '10px 12px',
    background: 'linear-gradient(135deg, var(--accent-dim), transparent)', border: '1px solid var(--accent)',
    borderRadius: 10, textDecoration: 'none', position: 'relative', cursor: 'pointer',
    width: 'calc(100% - 24px)', boxSizing: 'border-box'
  },
  extPromoText: { flex: 1, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 },
  extPromoTitle: { fontSize: 12, fontWeight: 700, color: 'var(--accent)' },
  extPromoSubtitle: { fontSize: 10, color: 'var(--text-muted)' },
  extPromoClose: {
    background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer',
    padding: 2, display: 'flex', borderRadius: 4, flexShrink: 0
  }
};
